import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useBlogArticles } from '@/hooks/useBlogArticles';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * "From the Blog" homepage section — shows 3 latest articles
 * fetched dynamically from Shopify Storefront API.
 */
export function BlogPreview() {
  const { t, i18n } = useTranslation();
  const isIT = i18n.language === 'it' || !i18n.language?.startsWith('en');
  const locale = isIT ? 'it-IT' : 'en-US';

  const { data: allArticles, isLoading } = useBlogArticles(3);

  if (isLoading) {
    return (
      <section className="py-16 md:py-20 bg-secondary/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-10">
            <Skeleton className="h-4 w-24 mx-auto mb-2" />
            <Skeleton className="h-8 w-48 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg overflow-hidden bg-card border border-border">
                <Skeleton className="aspect-[16/9] w-full" />
                <div className="p-5 space-y-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!allArticles || allArticles.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-secondary/30">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <p className="font-elegant text-sm uppercase tracking-[0.2em] text-bronze mb-2">
            {t('blog.label', 'Our Blog')}
          </p>
          <h2 className="font-display text-2xl md:text-3xl text-foreground">
            {isIT ? 'Dal Nostro Blog' : 'From the Blog'}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {allArticles.map((article, idx) => (
            <motion.div
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <Link
                to={`/blog/${article.handle}`}
                className="group block rounded-lg overflow-hidden bg-card border border-border card-hover h-full"
              >
                {article.image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image.url}
                      alt={article.image.altText || article.title}
                      className="w-full h-full object-cover img-zoom"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-5">
                  <time className="text-xs text-muted-foreground uppercase tracking-wider">
                    {new Date(article.publishedAt).toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </time>
                  <h3 className="font-heading text-lg font-semibold text-foreground mt-2 mb-2 line-clamp-2 group-hover:text-bronze transition-colors">
                    {article.title}
                  </h3>
                  {article.excerpt && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {article.excerpt}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 text-sm text-bronze mt-3 group-hover:gap-2 transition-all">
                    {t('blog.readMore', 'Read more')}
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-bronze hover:text-bronze/80 font-medium transition-colors"
          >
            {isIT ? 'Tutti gli Articoli' : 'All Articles'}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
